import React, { useState, useRef, useEffect } from "react";
import moment from "moment";
import api from "../../services/api";
import { useMenuAuthInfo } from "../../services/GlobalUtil";
import { useTranslation } from "react-i18next";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  ResponsiveContainer,
} from "recharts";
import BreadCrumbDiv from "../../components/common/BreadCrumbDiv";
import FunctionButton from "../../components/common/FunctionButton";
import ButtonWrapDiv from "../../components/common/ButtonWrapDiv";
import SearchWrapDiv from "../../components/common/SearchWrapDiv";
import CustomDropDownTreeSelect from "../../components/common/CustomDropDownTreeSelect";
import CustomDatePicker from "../../components/common/CustomDatePicker";
import SearchButton from "../../components/common/SearchButton";
import PeriodQuickButtons from "../../components/common/PeriodQuickButtons";
import SupportFilterButtons from "../../components/common/SupportFilterButtons";
import LoadingBoxDiv from "../../components/common/LoadingBoxDiv";
import { saveBlobAsFile } from "../../services/FileDownloadUtil";

// 세로 막대차트 색상 팔레트 (페이지당 10개) - 서로 구분이 잘 되는 카테고리 색상
const BAR_COLORS = [
  "#1f4e9c", // blue
  "#e2231a", // red
  "#f2a900", // amber
  "#2e9e5b", // green
  "#7b3ff2", // purple
  "#00a3a3", // teal
  "#e8730c", // orange
  "#c2185b", // magenta
  "#6d4c41", // brown
  "#546e7a", // blue-grey
];

// 영역별 한 화면에 표시하는 항목 수 (전체 TOP 50 을 좌우 화살표로 페이지 이동)
const PAGE_SIZE = 10;

// 영역별 페이지 상태: 데이터가 바뀌면(재조회) 첫 페이지로 돌아간다
const usePagedData = (data) => {
  const [page, setPage] = useState(0);
  useEffect(() => {
    setPage(0);
  }, [data]);
  const list = data || [];
  const pageCount = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageData = list.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE);
  return { page: safePage, pageCount, pageData, setPage, totalCount: list.length };
};

const PageArrowButton = ({ direction, disabled, onClick }) => (
  <button
    type="button"
    title={direction === "prev" ? "Previous" : "Next"}
    onClick={onClick}
    disabled={disabled}
    style={{
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      width: "24px",
      height: "24px",
      padding: 0,
      fontSize: "14px",
      lineHeight: 1,
      color: disabled ? "#bbb" : "#002c5f",
      background: "#fff",
      border: `1px solid ${disabled ? "#ddd" : "#8a9bb3"}`,
      borderRadius: "3px",
      cursor: disabled ? "default" : "pointer",
    }}
  >
    {direction === "prev" ? "◀" : "▶"}
  </button>
);

// 영역 헤더: 제목 + 현재 순위 범위 + 좌우 화살표
const PagedChartHeader = ({ title, page, pageCount, totalCount, setPage }) => {
  const from = totalCount === 0 ? 0 : page * PAGE_SIZE + 1;
  const to = Math.min((page + 1) * PAGE_SIZE, totalCount);
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        background: "#efece6",
        padding: "6px 14px",
        minHeight: "38px",
        boxSizing: "border-box",
        fontWeight: "bold",
        borderBottom: "1px solid #e0e0e0",
      }}
    >
      <span>{title}</span>
      {totalCount > 0 && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
          <span style={{ fontSize: "12px", fontWeight: "normal", color: "#555" }}>
            {from} - {to} / {totalCount}
          </span>
          <PageArrowButton
            direction="prev"
            disabled={page <= 0}
            onClick={() => setPage(page - 1)}
          />
          <PageArrowButton
            direction="next"
            disabled={page >= pageCount - 1}
            onClick={() => setPage(page + 1)}
          />
        </span>
      )}
    </div>
  );
};

const DefectTrendAnalysisPage = () => {
  const menuAuthInfo = useMenuAuthInfo();
  const { t } = useTranslation();

  const [startDate, setStartDate] = useState(
    moment().subtract(1, "week").add(1, "day").toDate()
  );
  const [endDate, setEndDate] = useState(moment().toDate());
  const [period, setPeriod] = useState("week");
  const [sbLocationDlr, setSbLocationDlr] = useState([]);
  const [sbCar, setSbCar] = useState([]);
  const [locationDlr, setLocationDlr] = useState([]);
  const [car, setCar] = useState([]);
  const [supportFilter, setSupportFilter] = useState("ALL");

  const searchRegion = useRef([]);
  const searchCar = useRef([]);
  const searchSupportFilter = useRef("ALL");
  const searchStartDt = useRef(
    moment().subtract(1, "week").add(1, "day").format("YYYY-MM-DD 00:00:00")
  );
  const searchEndDt = useRef(moment().format("YYYY-MM-DD 00:00:00"));

  const [inData, setInData] = useState([]);
  const [outData, setOutData] = useState([]);
  const [inTotal, setInTotal] = useState(0);
  const [outTotal, setOutTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isExcelLoading, setIsExcelLoading] = useState(false);
  const [vinExcelLoadingKey, setVinExcelLoadingKey] = useState(null);

  useEffect(() => {
    if (menuAuthInfo?.view) {
      loadContentData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuAuthInfo?.view]);

  const loadLocationLocData = () => {
    if (sbLocationDlr != null && sbLocationDlr.length > 0) {
      return;
    }
    api
      .post("/common/location-loc-tree", {})
      .then((response) => {
        setSbLocationDlr(response.data);
      })
      .catch(() => {});
  };

  const fetchCarData = (filter) => {
    api
      .post("/common/car-tree", { sSupportFilter: filter })
      .then((response) => {
        setSbCar(response.data);
      })
      .catch(() => {});
  };

  const loadCarData = () => {
    if (sbCar != null && sbCar.length > 0) {
      return;
    }
    fetchCarData(supportFilter);
  };

  const handleSupportFilterSelect = (key) => {
    if (key === supportFilter) {
      return;
    }
    setSupportFilter(key);
    setCar([]);
    setSbCar([]);
    fetchCarData(key);

    // 지원/미지원 선택 시 차트도 즉시 반영
    searchRegion.current = locationDlr;
    searchCar.current = [];
    searchSupportFilter.current = key;
    searchStartDt.current = moment(startDate).format("YYYY-MM-DD 00:00:00");
    searchEndDt.current = moment(endDate).format("YYYY-MM-DD 00:00:00");
    loadContentData();
  };

  const handleStartDateChange = (data) => {
    setStartDate(data);
    setPeriod("");
    if (moment(data).isAfter(moment(endDate))) {
      setEndDate(data);
    }
  };

  const handleEndDateChange = (data) => {
    setEndDate(data);
    setPeriod("");
    if (moment(data).isBefore(moment(startDate))) {
      setStartDate(data);
    }
  };

  const handlePeriodSelect = (start, end, key) => {
    setStartDate(start);
    setEndDate(end);
    setPeriod(key);

    searchRegion.current = locationDlr;
    searchCar.current = car;
    searchSupportFilter.current = supportFilter;
    searchStartDt.current = moment(start).format("YYYY-MM-DD 00:00:00");
    searchEndDt.current = moment(end).format("YYYY-MM-DD 00:00:00");
    loadContentData();
  };

  const toChartData = (list) =>
    (list || []).map((item) => ({
      rank: item.rankNo,
      itemCode: item.itemCode,
      name: item.inspItem,
      count: item.ngCount,
    }));

  const loadContentData = () => {
    setIsLoading(true);
    api
      .post("/quality-analysis/defect-trend/top-ng", {
        sRegions: searchRegion.current,
        sVehicles: searchCar.current,
        sSupportFilter: searchSupportFilter.current,
        sStartDate: searchStartDt.current,
        sEndDate: searchEndDt.current,
      })
      .then((response) => {
        setInData(toChartData(response.data.inList));
        setOutData(toChartData(response.data.outList));
        setInTotal(response.data.inTotal || 0);
        setOutTotal(response.data.outTotal || 0);
        setIsLoading(false);
      })
      .catch(() => {
        setIsLoading(false);
      });
  };

  const handleSearch = () => {
    searchRegion.current = locationDlr;
    searchCar.current = car;
    searchSupportFilter.current = supportFilter;
    searchStartDt.current = moment(startDate).format("YYYY-MM-DD 00:00:00");
    searchEndDt.current = moment(endDate).format("YYYY-MM-DD 00:00:00");
    loadContentData();
  };

  const handleDownloadExcel = () => {
    if (isExcelLoading) {
      return;
    }
    setIsExcelLoading(true);
    api
      .post(
        "/quality-analysis/defect-trend/excel",
        {
          sRegions: searchRegion.current,
          sVehicles: searchCar.current,
          sSupportFilter: searchSupportFilter.current,
          sStartDate: searchStartDt.current,
          sEndDate: searchEndDt.current,
        },
        {
          responseType: "blob",
        }
      )
      .then((response) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          "Defect_Trend_Analysis_" + moment().format("YYYYMMDD") + ".xlsx"
        );
        document.body.appendChild(link);
        link.click();
        link.remove();
        setIsExcelLoading(false);
      })
      .catch(() => {
        setIsExcelLoading(false);
      });
  };

  // NG 항목별 VIN 리스트 엑셀 (범례 항목 우측 버튼)
  const handleDownloadVinExcel = (inspType, entry) => {
    const key = `${inspType}-${entry.itemCode}`;
    if (vinExcelLoadingKey) {
      return;
    }
    setVinExcelLoadingKey(key);
    api
      .post(
        "/quality-analysis/defect-trend/vin-excel",
        {
          sRegions: searchRegion.current,
          sVehicles: searchCar.current,
          sSupportFilter: searchSupportFilter.current,
          sStartDate: searchStartDt.current,
          sEndDate: searchEndDt.current,
          sInspType: inspType,
          sItemCode: entry.itemCode,
          sInspItem: entry.name,
        },
        {
          responseType: "blob",
        }
      )
      .then((response) => {
        const safeName = (entry.name || entry.itemCode).replace(
          /[\\/:*?"<>|]/g,
          "_"
        );
        saveBlobAsFile(
          response.data,
          `NG_VIN_${inspType}_${safeName}_${moment().format("YYYYMMDD")}.xlsx`
        );
        setVinExcelLoadingKey(null);
      })
      .catch(() => {
        setVinExcelLoadingKey(null);
      });
  };

  return (
    <div>
      <BreadCrumbDiv
        items={[t("MENU:L1100"), t("MENU:L1102")]}
      ></BreadCrumbDiv>

      <SearchWrapDiv>
        <ButtonWrapDiv>
          <CustomDropDownTreeSelect
            data={sbLocationDlr}
            placeholder={t("L0049")}
            isViewCode={true}
            flattenIntermediate={true}
            onChange={setLocationDlr}
            onFocus={loadLocationLocData}
          ></CustomDropDownTreeSelect>
          <CustomDropDownTreeSelect
            key={`car-${supportFilter}`}
            data={sbCar}
            placeholder={t("L0672")}
            onChange={setCar}
            onFocus={loadCarData}
          ></CustomDropDownTreeSelect>
        </ButtonWrapDiv>
        <ButtonWrapDiv className="btn_right">
          <CustomDatePicker
            defaultDate={startDate}
            maxDate={new Date()}
            onChange={(date) => handleStartDateChange(date)}
            dateFormat={"YYYY-MM-dd"}
          ></CustomDatePicker>
          <div>
            <br />
            &nbsp;&nbsp;-&nbsp;&nbsp;
          </div>
          <CustomDatePicker
            defaultDate={endDate}
            maxDate={new Date()}
            onChange={(date) => handleEndDateChange(date)}
            dateFormat={"YYYY-MM-dd"}
          ></CustomDatePicker>
          <SearchButton onClick={handleSearch}></SearchButton>
        </ButtonWrapDiv>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "10px",
          }}
        >
          <SupportFilterButtons
            selected={supportFilter}
            onSelect={handleSupportFilterSelect}
          />
          <PeriodQuickButtons selected={period} onSelect={handlePeriodSelect} />
        </div>
      </SearchWrapDiv>

      <ButtonWrapDiv className="btn_top">
        <FunctionButton
          value={t("L0150")}
          onClick={handleDownloadExcel}
          loading={isExcelLoading}
        ></FunctionButton>
      </ButtonWrapDiv>

      <LoadingBoxDiv loading={isLoading}>
        <div
          style={{
            display: "flex",
            gap: "20px",
            flexWrap: "wrap",
            alignItems: "flex-start",
          }}
        >
          <TopNgChart
            title="IN TOP 50"
            data={inData}
            total={inTotal}
            emptyText={t("L0094")}
          />
          <TopNgChart
            title="OUT TOP 50"
            data={outData}
            total={outTotal}
            emptyText={t("L0094")}
          />
        </div>

        <div
          style={{
            display: "flex",
            gap: "20px",
            flexWrap: "wrap",
            alignItems: "flex-start",
            marginTop: "20px",
          }}
        >
          <TopNgBarChart
            title="IN TOP 50"
            data={inData}
            emptyText={t("L0094")}
            onDownload={(entry) => handleDownloadVinExcel("IN", entry)}
            loadingItemCode={
              vinExcelLoadingKey?.startsWith("IN-")
                ? vinExcelLoadingKey.substring(3)
                : null
            }
          />
          <TopNgBarChart
            title="OUT TOP 50"
            data={outData}
            emptyText={t("L0094")}
            onDownload={(entry) => handleDownloadVinExcel("OUT", entry)}
            loadingItemCode={
              vinExcelLoadingKey?.startsWith("OUT-")
                ? vinExcelLoadingKey.substring(4)
                : null
            }
          />
        </div>
      </LoadingBoxDiv>
    </div>
  );
};

const BAR_FILL_COLOR = "#4a90d9";

const TopNgChart = ({ title, data, total, emptyText }) => {
  const [hoverIndex, setHoverIndex] = useState(null);
  const { page, pageCount, pageData, setPage, totalCount } = usePagedData(data);
  // 최대 건수 (막대 길이 스케일 기준: 전체 TOP 50 중 최대 건수 항목이 가장 긴 막대,
  // 페이지를 넘겨도 동일 기준으로 비교 가능)
  const maxCount = Math.max(...(data || []).map((entry) => entry.count || 0), 1);
  return (
    <div
      style={{
        flex: "1 1 480px",
        minWidth: "460px",
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        background: "#fff",
        boxSizing: "border-box",
      }}
    >
      <PagedChartHeader
        title={title}
        page={page}
        pageCount={pageCount}
        totalCount={totalCount}
        setPage={setPage}
      />
      <div style={{ padding: "14px" }}>
        {pageData.length === 0 ? (
          <div
            style={{
              height: "260px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#999",
            }}
          >
            {emptyText}
          </div>
        ) : (
          <div>
            {pageData.map((entry, index) => {
              // 오른쪽 텍스트: 전체 NG 대비 비율
              const pct =
                total > 0 ? ((entry.count / total) * 100).toFixed(2) : "0.00";
              // 막대 길이: 최대 건수 대비 (최대 항목이 가장 길고 나머지는 비례)
              const barWidth = (entry.count / maxCount) * 100;
              const isHover = hoverIndex === index;
              return (
                <div
                  key={`row-${index}`}
                  onMouseEnter={() => setHoverIndex(index)}
                  onMouseLeave={() => setHoverIndex(null)}
                  style={{
                    display: "grid",
                    // 가장 긴 항목명("Airbag Resistance FL_Side Inflator")이
                    // 들어가는 고정 폭 → 모든 행의 막대/값 정렬 유지
                    gridTemplateColumns: "255px 1fr max-content",
                    alignItems: "center",
                    columnGap: "14px",
                    padding: "5px 8px",
                    borderRadius: "3px",
                    background: isHover
                      ? "#cddff1"
                      : index % 2 === 0
                      ? "#edf1f6"
                      : "#ffffff",
                  }}
                >
                  <span
                    style={{
                      fontSize: "12px",
                      color: "#333",
                      whiteSpace: "nowrap",
                      fontWeight: isHover ? "bold" : "normal",
                    }}
                  >
                    {entry.name}
                  </span>
                  <div style={{ height: "20px" }}>
                    <div
                      style={{
                        width: `${barWidth}%`,
                        height: "100%",
                        background: BAR_FILL_COLOR,
                        borderRadius: "3px",
                        boxSizing: "border-box",
                        border: isHover
                          ? "1px solid #002c5f"
                          : "1px solid transparent",
                        transition: "width 0.2s ease",
                      }}
                    ></div>
                  </div>
                  <span
                    style={{
                      fontSize: "12px",
                      textAlign: "right",
                      whiteSpace: "nowrap",
                      fontWeight: isHover ? "bold" : "normal",
                    }}
                  >
                    <b style={{ color: "#222" }}>
                      {entry.count?.toLocaleString?.() ?? entry.count}
                    </b>
                    <span style={{ color: "#888", marginLeft: "5px" }}>
                      ({pct}%)
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const CustomTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const d = payload[0].payload;
    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid #ccc",
          padding: "8px 10px",
          fontSize: "12px",
          maxWidth: "260px",
        }}
      >
        <div style={{ fontWeight: "bold" }}>{d.name}</div>
        <div>Count: {d.count?.toLocaleString?.() ?? d.count}</div>
      </div>
    );
  }
  return null;
};

// 범례 항목별 VIN 리스트 엑셀 다운로드 버튼
const VinExcelButton = ({ loading, disabled, onClick }) => (
  <button
    type="button"
    title="VIN List Excel Download"
    onClick={onClick}
    disabled={disabled}
    style={{
      marginLeft: "auto",
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      gap: "4px",
      height: "20px",
      padding: "0 6px",
      fontSize: "11px",
      color: "#1d6f42",
      background: "#fff",
      border: "1px solid #1d6f42",
      borderRadius: "3px",
      cursor: disabled ? "default" : "pointer",
      opacity: disabled && !loading ? 0.4 : 1,
    }}
  >
    <svg width="12" height="12" viewBox="0 0 16 16" aria-hidden="true">
      <rect x="1" y="1" width="14" height="14" rx="2" fill="#1d6f42" />
      <path
        d="M5 4.5 L11 11.5 M11 4.5 L5 11.5"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
    {loading ? "..." : "VIN"}
  </button>
);

// 기존 세로 막대차트 (색상 막대 + 범례)
const TopNgBarChart = ({
  title,
  data,
  emptyText,
  onDownload,
  loadingItemCode,
}) => {
  const [hoverIndex, setHoverIndex] = useState(null);
  const { page, pageCount, pageData, setPage, totalCount } = usePagedData(data);
  return (
    <div
      style={{
        flex: "1 1 460px",
        minWidth: "420px",
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        background: "#fff",
        boxSizing: "border-box",
      }}
    >
      <PagedChartHeader
        title={title}
        page={page}
        pageCount={pageCount}
        totalCount={totalCount}
        setPage={setPage}
      />
      <div style={{ padding: "14px" }}>
        {pageData.length === 0 ? (
          <div
            style={{
              height: "260px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#999",
            }}
          >
            {emptyText}
          </div>
        ) : (
          <>
            <div style={{ width: "100%", height: "260px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={pageData}
                  margin={{ top: 24, right: 10, left: 0, bottom: 4 }}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="rank" tickLine={false} fontSize={12} />
                  <YAxis allowDecimals={false} fontSize={12} />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: "#f5f5f5" }} />
                  <Bar dataKey="count" isAnimationActive={false}>
                    <LabelList dataKey="count" position="top" fontSize={12} />
                    {pageData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={BAR_COLORS[index % BAR_COLORS.length]}
                        fillOpacity={
                          hoverIndex === null || hoverIndex === index ? 1 : 0.2
                        }
                        stroke={hoverIndex === index ? "#333" : "none"}
                        strokeWidth={hoverIndex === index ? 1 : 0}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            <ul
              style={{
                listStyle: "none",
                margin: "12px 0 0",
                padding: 0,
                fontSize: "12px",
              }}
            >
              {pageData.map((entry, index) => (
                <li
                  key={`legend-${index}`}
                  onMouseEnter={() => setHoverIndex(index)}
                  onMouseLeave={() => setHoverIndex(null)}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "8px",
                    marginBottom: "4px",
                    padding: "2px 4px",
                    borderRadius: "3px",
                    cursor: "default",
                    background: hoverIndex === index ? "#f0f4fa" : "transparent",
                  }}
                >
                  <span
                    style={{
                      display: "inline-block",
                      width: "12px",
                      height: "12px",
                      marginTop: "2px",
                      background: BAR_COLORS[index % BAR_COLORS.length],
                      flexShrink: 0,
                    }}
                  ></span>
                  <span>
                    <b style={{ marginRight: "6px" }}>{entry.rank}.</b>
                    {entry.name}
                    <span style={{ color: "#888", marginLeft: "6px" }}>
                      ({entry.count?.toLocaleString?.() ?? entry.count})
                    </span>
                  </span>
                  <VinExcelButton
                    loading={loadingItemCode === entry.itemCode}
                    disabled={loadingItemCode != null}
                    onClick={() => onDownload(entry)}
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

export default DefectTrendAnalysisPage;
